import { type FC } from "react";
import { twMerge } from "tailwind-merge";

import { SessionButton } from "./SessionButton";
import { ImportCourseButton } from "./content/ImportSectionButton";

import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentCourseCodeTitle,
  selectCurrentCourseDescription,
  selectCurrentCourseHasMaterials,
  selectCurrentCourseId,
  selectCurrentCourseTitle,
  selectCurrentOrgName,
  selectCurrentTermTitle,
} from "store/selectors";

type CourseTitleBarProps = {
  className?: string;
};

export const CourseTitleBar: FC<CourseTitleBarProps> = ({ className }) => {
  const courseId = useAppSelector(selectCurrentCourseId);
  const courseTitle = useAppSelector(selectCurrentCourseTitle);
  const courseDescription = useAppSelector(selectCurrentCourseDescription);
  const { canAuthorCourse } = useCapabilities();
  const orgName = useAppSelector(selectCurrentOrgName);
  const courseCodeTitle = useAppSelector(selectCurrentCourseCodeTitle);
  const termTitle = useAppSelector(selectCurrentTermTitle);
  const hasMaterials = useAppSelector(selectCurrentCourseHasMaterials);

  if (!courseId) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex w-full flex-col justify-between gap-4 sm:flex-row sm:items-center",
        className,
      )}
    >
      <div className="flex w-full flex-col items-baseline gap-2 xl:flex-row xl:gap-4">
        <div className="flex flex-row items-center gap-4">
          <h2 className="flex items-center justify-between text-2xl text-black-tint-20">
            {courseTitle}
          </h2>

          {canAuthorCourse && !hasMaterials && <ImportCourseButton />}
        </div>

        {orgName && termTitle && courseCodeTitle && (
          <div className="flex flex-row items-center gap-2 border-black text-base text-black-tint-20 xl:border-l xl:pl-4">
            {orgName}, {termTitle} - {courseCodeTitle}
            {courseDescription && (
              <Tooltip label={t("tooltip.view.course_description")} text={courseDescription} />
            )}
          </div>
        )}
      </div>

      <SessionButton />
    </div>
  );
};
